import { DashboardState, ESdChangeReason, IHelpdeskLimits, ViewPart, Visibility } from "@kuika/kuika-cl-model";
import classNames from "classnames";
import React from "react";
import { IWithSDProps, withSD } from "../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../shared/hoc/with-context";
import { IWithHistoryProp, withHistory } from "../../shared/hoc/with-history";
import { IWithWindowSizeProp, withWindowSize } from "../../shared/hoc/with-window-size";
import { PureSdComponent } from "../../shared/pure-sd";
import { shouldShowReportCanvasSkeleton } from "../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../shared/utility/main-functions";
import { AccessLimitedModal } from "../common/access-limited-modal";
import { SaveCustomBlockModal } from "../common/custom-blocks/save-custom-block-modal";
import { ElementPickerWithActionModal } from "../common/element-picker-with-action-modal";
import { FormWizard } from "../common/form-wizard";
import { ImportMappedExcelSetupWizard } from "../common/import-mapped-excel-setup-wizard/import-mapped-excel-setup-wizard";
import { PickProcessModal } from "../common/pick-process-modal";
import { ShortcutListenerForNavigation } from "../common/shortcut-listeners/shortcut-listener-for-navigation";
import { ShortcutListenerForToolbar } from "../common/shortcut-listeners/shortcut-listener-for-toolbar";
import { StepScreenFormWizard } from "../common/step-screen-form-wizard/step-screen-form-wizard";
import UnsupportedDeviceOverlay from "../common/unsupported-device-overlay";
import KuikaButton from "../ui-kit/kuika-button/kuika-button";
import KuikaSpinner from "../ui-kit/kuika-spinner/kuika-spinner";
import KuikaSvg from "../ui-kit/kuika-svg/kuika-svg";
import { ApiManagementCanvas } from "./api-management/api-management-canvas";
import { AppSettingsCanvas } from "./app-settings/app-settings-canvas";
import { RunSettings } from "./app-settings/run-settings";
import { BranchingCanvas } from "./branching/branching-canvas";
import { Canvas } from "./canvas/canvas";
import { McpCanvasBackgroundAnimation } from "./canvas/mcp-canvas-border-animation";
import { SelectionOverlay } from "./canvas/selection-overlay/selection-overlay";
import { ZoomControls } from "./canvas/zoom-controls";
import { DataDiagramCanvas } from "./data-diagram/data-diagram-canvas";
import { DocumentParametersCanvas } from "./document-parameters/document-parameters-canvas";
import { DocumentParametersModal } from "./document-parameters/document-parameters-modal";
import { WorkFlowWindow } from "./flows/work-flow-window";
import { DesignSubBar } from "./header/sub-bar/design-sub-bar";
import { ToolBar } from "./header/toolbar/toolbar";
import HelpdeskProviderSwitch from "./helpdesk/helpdesk-provider-switch";
import { ItsmHelpdeskConnected } from "./helpdesk/itsm-helpdesk-connected";
import { DesignLeftMenu } from "./left-menu/design-left-menu";
import { DesignLeftPanel } from "./left-panel/design-left-panel";
import { NotificationTemplateCanvas } from "./notification-template/notification-template-canvas";
import { CreateReportDesigner } from "./report-designer/create-report-design";
import { DesignRightMenu } from "./right-menu/design-right-menu";
import { DesignRightPanel } from "./right-panel/design-right-panel";
import { ScheduledJobsCanvas } from "./scheduled-jobs/scheduled-jobs-canvas";
import { ZendeskCanvas } from "./zendesk/zendesk-canvas";

declare let window: Window & { skipAlert: boolean };

export interface IDesignerDashboardState {
  zoomScale: number;
  /** The full-page Helpdesk was opened to write to support ("Contact us"): start on the form. */
  helpdeskStartsWithNewTicket: boolean;
}

export interface IDesignerDashboardProps {}

class DesignerDashboard extends PureSdComponent<
  IDesignerDashboardProps & IWithSDProps & IWithContextProp & IWithHistoryProp & IWithWindowSizeProp,
  IDesignerDashboardState
> {
  constructor(
    props: IDesignerDashboardProps & IWithSDProps & IWithContextProp & IWithHistoryProp & IWithWindowSizeProp
  ) {
    super(props);
    this.state = {
      zoomScale: 1,
      helpdeskStartsWithNewTicket: false
    };
  }

  componentDidUpdate(): void {
    // The request is for that one visit; other ways into the Helpdesk open on the ticket list.
    if (this.state.helpdeskStartsWithNewTicket && this.props.sd.dashboardState !== DashboardState.zendesk) {
      this.setState({ helpdeskStartsWithNewTicket: false });
    }
  }

  saveScreenOnUnmount = async (): Promise<any> => {
    if (document.visibilityState === "hidden" && this.props.sd.isProjectLoaded === true) {
      try {
        await this.props.sd.saveScreen(true);
      } catch (error) {
        console.error("Error saving screen on visibility change:", error);
      }
    }
  };

  beforeunload = async (event: BeforeUnloadEvent) => {
    if (this.props.sd.isProjectLoaded === true && window.skipAlert !== true) {
      event.preventDefault();

      try {
        await this.props.sd.saveScreen(true);
      } catch (error) {
        console.error("Error saving screen on beforeunload:", error);
      }

      return "Changes you made may not be saved.";
    }
  };

  async componentDidMount() {
    if (KMainFunctions.getIsMobile()) {
      this.props.navigator.gotoUnsupportedDevice();
      return;
    }

    document.addEventListener("visibilitychange", this.saveScreenOnUnmount);
    window.addEventListener("beforeunload", this.beforeunload);
  }

  componentWillUnmount() {
    document.removeEventListener("visibilitychange", this.saveScreenOnUnmount);
    window.removeEventListener("beforeunload", this.beforeunload);
    // Guarded: a designer replaced before its app loaded must not save what the shared store holds.
    void this.props.sd.saveScreenOnLeave();
  }

  handleZoomScale = (val: number) => {
    this.setState({ zoomScale: val });
  };

  getKuikaLayout = () => {
    let result = "kuika_layout";
    if (this.props.sd.isProjectLoaded !== true) {
      result += " kuika_iframeLoadingWrapper";
    }
    return result;
  };

  // Persistent edge affordance to re-open a collapsed panel. The panels unmount when
  // collapsed (getVisibility → Hidden), so this lives in the always-mounted center column,
  // pinned to that side's edge and shown only while the matching panel is collapsed.
  renderPanelExpandTab = (side: "left" | "right"): JSX.Element | null => {
    const sd = this.props.sd;
    const state: DashboardState = sd.dashboardState;
    if (side === "left") {
      // Workflow's only left panel is the on-demand datasources panel, which closes from its
      // own rail button rather than collapsing — its visibility gate never consults the
      // collapse map, so an expand tab here would be a dead control (ported from KOA-15840's
      // PanelEdgeToggle suppression).
      if (state === DashboardState.workFlow) return null;
      const collapsed: boolean = sd.isLeftPanelCollapsed[state];
      if (!collapsed) return null;
      return (
        <div className="kuika-panel-expand-tab kuika-panel-expand-tab--left">
          <KuikaButton
            data-testid="leftPanelExpandTab"
            data-onboarding-id="left-panel-expand"
            type="secondary"
            size="md"
            isIcon
            aria-label="Expand panel"
            style={{ boxShadow: "var(--shadow-sm)" }}
            onClick={() => sd.setLeftPanelCollapsed(false)}
          >
            <KuikaSvg name="layout-sidebar-left-expand" type="sidebar-icons" width={16} height={16} pointer />
          </KuikaButton>
        </div>
      );
    }
    const collapsed: boolean = sd.isRightPanelCollapsed[state];
    if (!collapsed) return null;
    return (
      <div className="kuika-panel-expand-tab kuika-panel-expand-tab--right">
        <KuikaButton
          data-testid="rightPanelExpandTab"
          data-onboarding-id="right-panel-expand"
          type="secondary"
          size="md"
          isIcon
          aria-label="Expand panel"
          style={{ boxShadow: "var(--shadow-sm)" }}
          onClick={() => sd.setRightPanelCollapsed(false)}
        >
          <KuikaSvg
            name="layout-sidebar-left-expand"
            type="sidebar-icons"
            width={16}
            height={16}
            pointer
            style={{ transform: "scaleX(-1)" }}
          />
        </KuikaButton>
      </div>
    );
  };

  render() {
    // Derived, not the raw landing flag: while the tool is open and the loaded screen is not a
    // report, the landing owns the centre — the report canvas never paints a UI screen.
    const isCreateReportDesignPageActive: boolean = this.props.sd.isReportDesignerLandingVisible();
    const isToolbarVisible = this.props.sd.getVisibility(ViewPart.Toolbar) === Visibility.Visible;
    // Row-2 (screen/report selector + breadcrumb + undo/redo, plus the report page-size group).
    // Scoped to the views the two-row layout targets — design and report designer — so the extra
    // chrome (and its height math, via kuika-shell--has-subbar) never affects the other views.
    // Report Designer's CREATE page is excluded: nothing there has a design open, so every
    // control in the strip would be empty or act on the stale previous screen (the same reason
    // workflow's gallery keeps the band down — see showWorkflowBar below).
    const showReportDesignerBar: boolean =
      this.props.sd.dashboardState === DashboardState.reportDesigner && !isCreateReportDesignPageActive;
    // Workflow joins row-2 through its ViewPart rather than a bare dashboard-state check
    // because its visibility carries a condition the others don't have: the band must stay
    // down while the "Create a New Process" gallery is open (KOA-15840 extracted the process
    // editor's own header into this band, so without it workflow has no switcher or tabs).
    const showWorkflowBar: boolean = this.props.sd.getVisibility(ViewPart.SecondaryToolbar) === Visibility.Visible;
    const showSubBar =
      isToolbarVisible &&
      (this.props.sd.dashboardState === DashboardState.design || showReportDesignerBar || showWorkflowBar);
    const isSubBarFullWidth: boolean = showSubBar && showWorkflowBar;
    return (
      <div
        className={classNames("kuika-shell", {
          "kuika-shell--has-subbar": showSubBar,
          "kuika-shell--subbar-full-width": isSubBarFullWidth
        })}
        aria-busy={this.props.sd.isProjectLoaded !== true}
      >
        {/* The report canvas draws its own page-shaped placeholder, so the scrim stands down
            there: over a canvas whose rulers and design area are already painted, a
            full-viewport spinner reads as a hang rather than as a page on its way. */}
        <KuikaSpinner
          spinning={this.props.sd.isProjectLoaded !== true && !shouldShowReportCanvasSkeleton(this.props.sd)}
        />
        {/* The one place loading is ANNOUNCED. The spinner and the panel skeletons are the
            sighted cue; skeletons are aria-hidden, so the owner speaks for them — once, here,
            rather than four times from four regions. Mounted UNCONDITIONALLY with its text
            swapped (the board and the table editor do the same): a live region that appears
            already containing its text is announced unreliably, because assistive tech
            watches for mutations INSIDE a region it is already observing. */}
        <span className="kuika-visually-hidden" role="status" aria-live="polite">
          {this.props.sd.isProjectLoaded !== true ? "Loading project…" : ""}
        </span>
        {isToolbarVisible && (
          <div className="kuika-shell__topbar">
            <ToolBar />
          </div>
        )}
        <div className={this.getKuikaLayout()}>
          {this.props.sd.getVisibility(ViewPart.LeftMenu) === Visibility.Visible && <DesignLeftMenu />}
          {this.props.sd.getVisibility(ViewPart.LeftPanel) === Visibility.Visible && <DesignLeftPanel />}
          {/* Row-2 spans the CANVAS COLUMN ONLY (KOA-18354): both panels sit beside the strip,
              flush under row-1, so the shell reads symmetrically and the two panels' bottom
              hairlines line up with it. Only the canvas and the strip share this column.
              Workflow is the exception: its right panel comes and goes with the editor tab
              (Diagram only), so a canvas-column strip would widen and narrow on every tab
              switch. There the strip spans the stage and the panel opens underneath it.
              The stage and its row are mounted in every view so this switch never changes
              the canvas's ancestry — a re-parent would remount the canvas iframe. */}
          <div className="kuika_layout__stage">
            {isSubBarFullWidth && <DesignSubBar />}
            <div className="kuika_layout__stage-row">
              <div className="kuika_layout__main">
                {showSubBar && !isSubBarFullWidth && <DesignSubBar />}
                <div className="kuika_center">
                  <McpCanvasBackgroundAnimation />
                  {isCreateReportDesignPageActive && <CreateReportDesigner />}
                  <Canvas zoomScale={this.state.zoomScale} onZoomChange={this.handleZoomScale} />
                  {/* Always mounted: it keeps its canvas-iframe listeners (and the geometry store's reset on
                      reload) alive in every tool, and renders only in design / report designer. */}
                  <SelectionOverlay />
                  {this.props.sd.dashboardState === DashboardState.reportDesigner &&
                    !isCreateReportDesignPageActive && (
                      <ZoomControls zoomScale={this.state.zoomScale} onZoomChange={this.handleZoomScale} />
                    )}
                  {this.props.sd && this.props.sd.dashboardState === DashboardState.workFlow && <WorkFlowWindow />}
                  {this.props.sd &&
                    (this.props.sd.dashboardState === DashboardState.dataDiagramScheme ||
                      this.props.sd.dashboardState === DashboardState.aiHub) && <DataDiagramCanvas />}
                  {this.props.sd && this.props.sd.dashboardState === DashboardState.configManager && (
                    <AppSettingsCanvas />
                  )}
                  {this.props.sd && this.props.sd.dashboardState === DashboardState.runSettings && <RunSettings />}
                  {this.props.sd && this.props.sd.dashboardState === DashboardState.scheduledJobs && (
                    <ScheduledJobsCanvas />
                  )}
                  {this.props.sd && this.props.sd.dashboardState === DashboardState.documentParameters && (
                    <DocumentParametersCanvas />
                  )}
                  {this.props.sd && this.props.sd.dashboardState === DashboardState.apiManagement && (
                    <ApiManagementCanvas />
                  )}
                  {this.props.sd && this.props.sd.dashboardState === DashboardState.notificationTemplate && (
                    <NotificationTemplateCanvas />
                  )}
                  {this.props.sd && this.props.sd.dashboardState === DashboardState.zendesk && (
                    <HelpdeskProviderSwitch
                      zendesk={<ZendeskCanvas />}
                      itsm={(limits: IHelpdeskLimits): React.ReactNode => (
                        <ItsmHelpdeskConnected
                          limits={limits}
                          startWithNewTicket={this.state.helpdeskStartsWithNewTicket}
                        />
                      )}
                    />
                  )}
                  {this.props.sd && this.props.sd.dashboardState === DashboardState.branching && <BranchingCanvas />}
                  {this.renderPanelExpandTab("left")}
                  {this.renderPanelExpandTab("right")}
                </div>
              </div>
              {/* Beside the canvas column, not inside it — the mirror of the left panel above. */}
              {this.props.sd.getVisibility(ViewPart.RightPanel) === Visibility.Visible && <DesignRightPanel />}
            </div>
          </div>
          {/* Outside the canvas branches: the popup form of the Document Parameters tool is opened from a Document
          component's properties and overlays whichever canvas the user was on, so it must not be tied to one. */}
          <DocumentParametersModal />
          {this.props.sd.getVisibility(ViewPart.RightMenu) === Visibility.Visible && <DesignRightMenu />}
          <UnsupportedDeviceOverlay />
          <ElementPickerWithActionModal />
          {this.props.sd.isWizardForFormOpen && (
            <FormWizard metadata={this.props.sd.metadataForFormWizard ?? undefined} />
          )}
          {this.props.sd.isPickProcessModalOpen && <PickProcessModal />}
          {this.props.sd.isImportMappedExcelSetupWizardOpen && <ImportMappedExcelSetupWizard />}

          <StepScreenFormWizard />

          <AccessLimitedModal
            showModal={this.props.sd.showAccessLimitedModal}
            onClose={() => {
              this.props.sd.handleChangeVisibilityOfAccessLimitedModal();
            }}
            onContactUsClicked={() => {
              this.props.sd.handleChangeVisibilityOfAccessLimitedModal();
              this.setState({ helpdeskStartsWithNewTicket: true });
              this.props.sd.setDashboardState(DashboardState.zendesk);
            }}
          />

          <ShortcutListenerForToolbar />
          <ShortcutListenerForNavigation />
          <SaveCustomBlockModal />
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (changeReason === ESdChangeReason.Loading) {
      return true;
    }
    return false;
  };
}

const designerDashboard = withWindowSize(
  withContext<IDesignerDashboardProps>(
    withHistory<IDesignerDashboardProps>(withSD<IDesignerDashboardProps>(DesignerDashboard))
  )
);
export { designerDashboard as DesignerDashboard };
